import {
  callIfFn,
  EntityPlacerBuilder,
  plannerHistoryHelper
} from "src/components/Planner/planner/history";
import {
  PlannerAction
} from "src/components/Planner/planner/history/planner-action";
import {ts} from "src/utils/translate"
import {isRoomOutside, uDev} from "src/components/Planner/planner/uDev";
import {removeDevices, uDevDOM} from "src/components/Planner/planner/uDev-dom";
import {
  createRoomAjax,
  deleteRoomAjax,
  modifyRoomAjax,
  moveEnframedDevsToRoom
} from "src/components/Planner/planner/planner";
import {PlannerGlobals} from "src/components/Planner/planner/planner-globals";
import {redrawDevices} from "src/components/Planner/planner/deviceDrawing";
import {
  wallModHelperDOM
} from "src/components/Planner/planner/wallModHelper-dom";
import {deepCopy} from "src/components/Planner/controller";
import {
  filterInvalidMeasurements
} from "src/components/Planner/planner/measurement";
import {
  refreshFloorView
} from "src/components/Planner/planner/plannerCacheInvalidation";

/* ROOM START */
export class CreateRoomAction extends PlannerAction {
  /**
   *
   * @param {Room} room
   */
  constructor(room, matSaveState, enframedD3s) {
    super();
    this.actionName = ts("p_do_action_room_new", "Creating room");
    this.room = deepCopy(room);
    this.changesMats = uDev.outerBorderExists() && !isRoomOutside(this.room, PlannerGlobals.outerBorder);
    this.matSaveState = this.changesMats ? matSaveState : {};
    this.enframedD3s = enframedD3s || [];
  }
  execute(callback) {
    var thisVar = this;
    var clonedRoom = deepCopy(this.room);
    var startWalls = deepCopy(clonedRoom.poly);
    var startId = clonedRoom.id;
    for (var i = 0; i < clonedRoom.poly.length; i++) {
      delete clonedRoom.poly[i].id;
      delete clonedRoom.poly[i].order_index;
    }
    createRoomAjax(clonedRoom, undefined, undefined, !!this.enframedD3s.length)
    .then(() => {
      var endId = clonedRoom.id;
      PlannerGlobals.plannerHistoryManager.roomIdChanged(startId, endId);
      clonedRoom.poly.forEach((p, idx) => {
        this.room.poly[idx].id = p.id;
        PlannerGlobals.plannerHistoryManager.wallIdChanged(startWalls[idx].id, p.id);
      });

      if (this.changesMats) {
        PlannerGlobals.wallSegmentation.recalcPreserveSave(this.matSaveState, true);
        return new EntityPlacerBuilder()
        .setMatSaveState(PlannerGlobals.wallSegmentation.getSaveDataV2())
        .run();
      }
    })
    .then(() => moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.enframedD3s), clonedRoom.id))
    .then(() => {
      if (this.enframedD3s.length) {
        refreshFloorView();
      }
      callIfFn(callback, true);
    })
    .catch((err) => {
      callIfFn(callback, false);
    });
  }
  undo(callback) {
    const thisVar = this;
    const moveEnframedTo = uDev.outerBorderExists() && isRoomOutside(this.room, PlannerGlobals.outerBorder) ? uDev.OUTDOORS_ROOM_ID : uDev.UNASSIGNED_ROOM_ID;
    moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.enframedD3s), moveEnframedTo)
    .then(() => deleteRoomAjax(deepCopy(this.room)))
    .then(() => {
      if (thisVar.changesMats) {
        return new EntityPlacerBuilder()
        .setMatSaveState(thisVar.matSaveState)
        .run();
      }
    })
    .then(() => {
      callIfFn(callback, true);
    })
    .catch(() => {
      callIfFn(callback, false);
    });
  }
  roomIdChanged(from, to) {
    if (this.equalAsString(this.room.id, from)) {
      this.room.id = to;
    }
    plannerHistoryHelper.updateMatStateRoomId(from, to, this.matSaveState);
  }
  wallIdChanged(from, to) {
    for (var i = 0; i < this.room.poly.length; i++) {
      if (this.equalAsString(this.room.poly[i].id, from)) {
        this.room.poly[i].id = to;
      }
    }
  }
}
export class ModifyRoomAction extends PlannerAction {
  /**
   *
   * @param {Room} fromRoom
   * @param {Room} toRoom
   * @param {D3Device[]} deletedD3s
   * @param {WallModConnection[]} wallModConns
   * @param {Group[]} groups
   * @param {MeasurementConnection[]} measures
   * @param {SerialConnection[]} serialConnections
   * @param {D3Device[]} enframedD3s
   * @param {D3Device[]} leftOutD3s
   */
  constructor(fromRoom, toRoom, deletedD3s, wallModConns, groups, measures, matSaveState, serialConnections, enframedD3s, leftOutD3s) {
    super();
    this.actionName = ts("p_do_action_room_mod", "Modifying room");
    this.fromRoom = fromRoom;
    this.toRoom = toRoom;
    this.deletedD3s = deletedD3s;
    this.wallModConns = wallModConns;
    this.groups = groups;
    this.measures = measures;
    this.changesMats = uDev.outerBorderExists() && (!isRoomOutside(this.fromRoom, PlannerGlobals.outerBorder) || !isRoomOutside(this.toRoom, PlannerGlobals.outerBorder));
    this.matSaveState = this.changesMats ? matSaveState : {};
    this.serialConnections = serialConnections;
    this.enframedD3s = enframedD3s;
    this.leftOutD3s = leftOutD3s;
  }
  execute(callback) {
    const whereToPlaceLeftOuts = uDev.outerBorderExists() && isRoomOutside(this.toRoom, PlannerGlobals.outerBorder) ? uDev.OUTDOORS_ROOM_ID : uDev.UNASSIGNED_ROOM_ID;

    modifyRoomAjax(deepCopy(this.toRoom))
    .then(() => Promise.all(this.wallModConns.map(wmc => wallModHelperDOM.removeWallModConn(wmc.id))))
    .then(() => removeDevices(uDevDOM.devicesOfD3Ds(this.deletedD3s), undefined, true))
    .then(() => moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.enframedD3s), this.toRoom.id))
    .then(() => moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.leftOutD3s), whereToPlaceLeftOuts))
    .then(() => {
      if (this.changesMats) {
        PlannerGlobals.wallSegmentation.recalcPreserveSave(this.matSaveState, true);
        return new EntityPlacerBuilder()
        .setMatSaveState(PlannerGlobals.wallSegmentation.getSaveDataV2())
        .run();
      }
    })
    .then(() => {
      filterInvalidMeasurements();
      refreshFloorView();
      redrawDevices();
      callIfFn(callback, true);
    })
    .catch((err) => {
      callIfFn(callback, false);
    });
  }
  undo(callback) {
    const whereToPutBackEnframeds = uDev.outerBorderExists() && isRoomOutside(this.fromRoom, PlannerGlobals.outerBorder) ? uDev.OUTDOORS_ROOM_ID : uDev.UNASSIGNED_ROOM_ID;

    modifyRoomAjax(deepCopy(this.fromRoom))
    .then(() => new EntityPlacerBuilder()
    .setD3Devs(this.deletedD3s)
    .setWallModConns(this.wallModConns)
    .setGroups(this.groups)
    .setMeasurements(this.measures)
    .setSerialConnections(this.serialConnections)
    .setMatSaveState(this.matSaveState)
    .run())
    .then(() => moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.leftOutD3s), this.fromRoom.id))
    .then(() => moveEnframedDevsToRoom(uDevDOM.devicesOfD3Ds(this.enframedD3s), whereToPutBackEnframeds))
    .then(() => {
      refreshFloorView();
      callIfFn(callback, true);
    })
    .catch((err) => {
      callIfFn(callback, false);
    });
  }
  roomIdChanged(from, to) {
    var thisVar = this;
    var changeDevs = false;
    if (this.equalAsString(this.fromRoom.id, from)) {
      this.fromRoom.id = to;
      changeDevs = true;
    }
    if (this.equalAsString(this.toRoom.id, from)) {
      this.toRoom.id = to;
      changeDevs = true;
    }
    if (changeDevs) {
      this.deletedD3s.forEach(function (d3d) {
        d3d.roomId = to;
        d3d.devices.forEach(function (dev) {
          dev.roomId = to;
        });
        if (d3d.connId) {
          d3d.serials.forEach(ser => {
            ser.roomId = to;
            ser.serObj.roomId = to;
            ser.serObj.devices.forEach(function (dev) {
              dev.roomId = to;
            });
          });
        }
      });
    }
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (room) {
        if (thisVar.equalAsString(room.roomId, from)) {
          room.roomId = to;
        }
      });
    });
    plannerHistoryHelper.updateMatStateRoomId(from, to, this.matSaveState);
  }
  wallIdChanged(from, to) {
    var thisVar = this;
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (wm) {
        wm.wallIds.forEach(function (id, idx) {
          if (thisVar.equalAsString(id, from)) {
            wm.wallIds[idx] = to;
          }
        });
      });
    });
    for (var i = 0; i < this.fromRoom.poly.length; i++) {
      if (this.equalAsString(this.fromRoom.poly[i].id, from)) {
        this.fromRoom.poly[i].id = to;
      }
    }
    for (var i = 0; i < this.toRoom.poly.length; i++) {
      if (this.equalAsString(this.toRoom.poly[i].id, from)) {
        this.toRoom.poly[i].id = to;
      }
    }
    plannerHistoryHelper.updateMeasurementWallIds(from, to, this.measures);
  }
  wallModIdChanged(from, to) {
    var thisVar = this;
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (wm) {
        if (thisVar.equalAsString(wm.id, from)) {
          wm.id = to;
        }
      });
    })
    plannerHistoryHelper.updateMeasurementWallModIds(from, to, this.measures);
  }
  deviceIdChanged(from, to) {
    var thisVar = this;

    this.groups.forEach(function (grp) {
      grp.deviceIds.forEach(function (id, idx) {
        if (thisVar.equalAsString(id, from)) {
          grp.deviceIds[idx] = to;
        }
      });
    });

    plannerHistoryHelper.updateD3DevicesDevIds(from, to, this.deletedD3s);
    plannerHistoryHelper.updateMeasurementDeviceIds(from, to, this.measures);
  }
  serialIdChanged(from, to) {
    plannerHistoryHelper.updateD3DevicesSerialIds(from, to, this.deletedD3s);
    plannerHistoryHelper.updateMeasurementSerialIds(from, to, this.measures);
    plannerHistoryHelper.updateSerialIdInConnections(from, to, this.serialConnections);
  }
  serialConnectionIdChanged(from, to) {
    plannerHistoryHelper.updateSerialConnectionId(from, to, this.serialConnections);
  }
}
export class DeleteRoomAction extends PlannerAction {
  /**
   *
   * @param {Room} room
   * @param {D3Device[]} d3Devs
   * @param {WallModConnection[]} wallModConns
   * @param {Group[]} groups
   * @param {MeasurementConnection[]} measures
   * @param {SerialConnection[]} serialConnections
   */
  constructor(room, d3Devs, wallModConns, groups, measures, matSaveState, serialConnections) {
    super();
    this.actionName = ts("p_do_action_room_del", "Deleting room");
    this.room = room;
    this.d3Devs = d3Devs;
    this.wallModConns = wallModConns;
    this.groups = groups;
    this.measures = measures;
    this.changesMats = uDev.outerBorderExists() && !isRoomOutside(this.room, PlannerGlobals.outerBorder);
    this.matSaveState = this.changesMats ? matSaveState : {};
    this.serialConnections = serialConnections;
  }
  execute(callback) {
    const thisVar = this;
    deleteRoomAjax(deepCopy(this.room), function () {
      if (thisVar.changesMats) {
        PlannerGlobals.wallSegmentation.recalcPreserveSave(thisVar.matSaveState, true);
        new EntityPlacerBuilder()
        .setMatSaveState(PlannerGlobals.wallSegmentation.getSaveDataV2())
        .run().then(() => callIfFn(callback, true)).catch(() => callIfFn(callback, false));
      }
      else {
        callIfFn(callback, true);
      }
    }, function () {
      callIfFn(callback, false);
    });
  }
  undo(callback) {
    var thisVar = this;
    var clonedRoom = deepCopy(this.room);
    var startWalls = deepCopy(clonedRoom.poly);
    var startId = clonedRoom.id;
    for (var i = 0; i < clonedRoom.poly.length; i++) {
      delete clonedRoom.poly[i].id;
      delete clonedRoom.poly[i].order_index;
    }
    createRoomAjax(clonedRoom, function () {
      var endId = clonedRoom.id;
      PlannerGlobals.plannerHistoryManager.roomIdChanged(startId, endId);
      clonedRoom.poly.forEach(function (p, idx) {
        thisVar.room.poly[idx].id = p.id;
        PlannerGlobals.plannerHistoryManager.wallIdChanged(startWalls[idx].id, p.id);
      });

      new EntityPlacerBuilder()
      .setD3Devs(thisVar.d3Devs)
      .setWallModConns(thisVar.wallModConns)
      .setGroups(thisVar.groups)
      .setMeasurements(thisVar.measures)
      .setSerialConnections(thisVar.serialConnections)
      .setMatSaveState(thisVar.matSaveState)
      .run()
      .done(function () {
        callIfFn(callback, true);
      })
      .fail(function () {
        callIfFn(callback, false);
      });

    }, function () {
      callIfFn(callback, false);
    });
  }
  roomIdChanged(from, to) {
    var thisVar = this;
    if (this.equalAsString(this.room.id, from)) {
      this.room.id = to;
      this.d3Devs.forEach(function (d3d) {
        d3d.roomId = to;
        d3d.devices.forEach(function (dev) {
          dev.roomId = to;
        });
        if (d3d.connId) {
          d3d.serials.forEach(ser => {
            ser.roomId = to;
            ser.serObj.roomId = to;
            ser.serObj.devices.forEach(function (dev) {
              dev.roomId = to;
            });
          });
        }
      });
    }
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (wm) {
        if (thisVar.equalAsString(wm.roomId, from)) {
          wm.roomId = to;
        }
      });
    });
    plannerHistoryHelper.updateMatStateRoomId(from, to, this.matSaveState);
  }
  wallIdChanged(from, to) {
    var thisVar = this;
    for (var i = 0; i < this.room.poly.length; i++) {
      if (this.equalAsString(this.room.poly[i].id, from)) {
        this.room.poly[i].id = to;
      }
    }
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (wm) {
        wm.wallIds.forEach(function (id, idx) {
          if (thisVar.equalAsString(id, from)) {
            wm.wallIds[idx] = to;
          }
        });
      });
    });
    plannerHistoryHelper.updateMeasurementWallIds(from, to, this.getMeasures());
  }
  /* wallModConnIdChanged(from, to) {
      var thisVar = this;
      this.wallModConns.forEach(function (wmc) {
          if (thisVar.equalAsString(wmc.id, from)) {
              wmc.id = to;
          }
      });
  } */
  wallModIdChanged(from, to) {
    var thisVar = this;
    this.wallModConns.forEach(function (wmc) {
      wmc.rooms.forEach(function (wm) {
        if (thisVar.equalAsString(wm.id, from)) {
          wm.id = to;
        }
      });
    });
    plannerHistoryHelper.updateMeasurementWallModIds(from, to, this.getMeasures());
  }
  deviceIdChanged(from, to) {
    var thisVar = this;

    this.groups.forEach(function (grp) {
      grp.deviceIds.forEach(function (id, idx) {
        if (thisVar.equalAsString(id, from)) {
          grp.deviceIds[idx] = to;
        }
      });
    });

    plannerHistoryHelper.updateD3DevicesDevIds(from, to, this.d3Devs);
    plannerHistoryHelper.updateMeasurementDeviceIds(from, to, this.getMeasures());
  }
  serialIdChanged(from, to) {
    plannerHistoryHelper.updateD3DevicesSerialIds(from, to, this.d3Devs);
    plannerHistoryHelper.updateMeasurementSerialIds(from, to, this.getMeasures());
    plannerHistoryHelper.updateSerialIdInConnections(from, to, this.serialConnections);
  }
  serialConnectionIdChanged(from, to) {
    plannerHistoryHelper.updateSerialConnectionId(from, to, this.serialConnections);
  }
  getMeasures() {
    var measures = [];
    if (this.measures) {
      measures = measures.concat(this.measures);
    }
    this.d3Devs.forEach(function (d3d) {
      measures = measures.concat(d3d.removedMeasures || []);
      d3d.devices.forEach(function (dev) {
        measures = measures.concat(dev.removedDevParams.removedMeasures);
      });
    });
    return measures;
  }
}
/* ROOM END */

export class ModifyMaterialsAction extends PlannerAction {
  constructor(oldSaveData, newSaveData) {
    super();
    this.actionName = "Modifying materials";
    this.oldSaveData = oldSaveData;
    this.newSaveData = newSaveData;
  }
  undo(callback) {
    /* wallSegmentation.loadStateWithoutChecks(this.oldSaveData); */
    PlannerGlobals.wallSegmentation.recalcPreserveSave(this.oldSaveData, true);
    const saveData = PlannerGlobals.wallSegmentation.getSaveDataV2();
    PlannerGlobals.wallSegmentation.saveState(saveData)
    .then(() => callIfFn(callback, true))
    .catch(() => callIfFn(callback, false))
  }
  execute(callback) {
    /* wallSegmentation.loadStateWithoutChecks(this.newSaveData); */
    PlannerGlobals.wallSegmentation.recalcPreserveSave(this.newSaveData, true);
    const saveData = PlannerGlobals.wallSegmentation.getSaveDataV2();
    PlannerGlobals.wallSegmentation.saveState(saveData)
    .then(() => callIfFn(callback, true))
    .catch(() => callIfFn(callback, false))
  }
}
